'use client'

import React, { useState } from 'react'
import { ArrowRight, Check, LucideIcon } from 'lucide-react'
import { AnimatePresence, motion, HTMLMotionProps } from 'framer-motion'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

function cn(...inputs: any[]) { return twMerge(clsx(inputs)) }

interface SlideSwapButtonProps extends HTMLMotionProps<'button'> {
  text?: string
  loadingText?: string
  successText?: string
  icon?: LucideIcon
  classes?: string
}

export default function SlideSwapButton({
  text = 'Button',
  loadingText = 'Processing...',
  successText = 'Complete!',
  icon: Icon = ArrowRight,
  classes,
  onClick,
  disabled,
  ...props
}: SlideSwapButtonProps) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle')

  const isIdle = status === 'idle'

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (status !== 'idle' || disabled) return

    setStatus('loading')
    
    // Call the original onClick if provided
    if (onClick) {
      // @ts-ignore - motion buttons passed standard clicks
      onClick(e)
    }

    // Simulate status change for animation feedback
    setTimeout(() => {
      setStatus('success')
      setTimeout(() => {
        setStatus('idle')
      }, 2000)
    }, 1500)
  }

  return (
    <motion.button
      className={cn(
        'group bg-primary relative flex min-w-40 items-center justify-center overflow-hidden rounded-xl py-3 px-6 font-bold text-white transition-all hover:bg-primary/95 shadow-[0_0_20px_rgba(0,102,255,0.3)]',
        status !== 'idle' && 'cursor-default px-10',
        disabled && 'opacity-50 cursor-not-allowed',
        classes
      )}
      onClick={handleClick}
      disabled={disabled || status !== 'idle'}
      layout
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      {...props}
    >
      <AnimatePresence mode='popLayout' initial={false}>
        <motion.div
          key={status}
          className='flex items-center justify-center gap-2'
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -15 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          {status === 'idle' ? (
            <div className="flex items-center justify-center gap-2">
              <span className="transition-transform duration-500 group-hover:-translate-x-1">
                {text}
              </span>
              <Icon className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
            </div>
          ) : status === 'loading' ? (
            <div className='flex items-center justify-center gap-2'>
              <div className='border-white/30 border-t-white h-4 w-4 animate-spin rounded-full border-2' />
              <span className='text-sm'>{loadingText}</span>
            </div>
          ) : (
            <div className='flex items-center justify-center gap-2'>
              <Check className='h-4 w-4' />
              <span className='text-sm'>{successText}</span>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </motion.button>
  )
}
